// Pure aggregation helpers for charts. Operate on whatever shape the
// /api/scalp/positions endpoint returns plus the /api/cycles list.

import type { ScalpPosition, BackendCycle, BackendTrade } from "@/store/api";

export interface EquityPoint {
  ts: string;        // ISO time of the close
  cumulative: number; // running sum of net P&L USD
}

export interface DailyPoint {
  date: string;     // YYYY-MM-DD UTC
  pnl: number;
  count: number;
}

export interface RBin {
  bin: string;      // human-readable, e.g. "[-1.0, -0.75)"
  midpoint: number; // for the x-axis
  count: number;
  positive: boolean;
}

/** Cumulative net P&L curve. Skips rows without a close time or pnl. */
export function equityCurve(rows: ScalpPosition[]): EquityPoint[] {
  // The DB query returns rows DESC by id (most recent first); we need ASC
  // for a cumulative curve. Sort by exit_time when present, fall back to id.
  const sorted = [...rows]
    .filter((r) => r.net_pnl_usd != null && r.exit_time)
    .sort((a, b) => {
      const at = a.exit_time ? new Date(a.exit_time).getTime() : 0;
      const bt = b.exit_time ? new Date(b.exit_time).getTime() : 0;
      return at - bt;
    });
  let cum = 0;
  return sorted.map((r) => {
    cum += Number(r.net_pnl_usd ?? 0);
    return { ts: r.exit_time as string, cumulative: round2(cum) };
  });
}

/** Sum of net P&L per UTC day, last `days` days (zero-padded). */
export function dailyPnL(rows: ScalpPosition[], days = 14): DailyPoint[] {
  const buckets = new Map<string, { pnl: number; count: number }>();
  for (const r of rows) {
    if (r.net_pnl_usd == null || !r.exit_time) continue;
    const day = (r.exit_time as string).slice(0, 10);
    const cur = buckets.get(day) ?? { pnl: 0, count: 0 };
    cur.pnl += Number(r.net_pnl_usd);
    cur.count += 1;
    buckets.set(day, cur);
  }
  // Build the last `days` days, even when empty.
  const out: DailyPoint[] = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now.getTime() - i * 86400_000);
    const key = d.toISOString().slice(0, 10);
    const b = buckets.get(key);
    out.push({ date: key, pnl: round2(b?.pnl ?? 0), count: b?.count ?? 0 });
  }
  return out;
}

/**
 * R-multiple histogram. R = net_pnl / risk_dollars, where risk_dollars =
 * (entry - stop) * qty. Skips rows where stop or qty are missing/zero.
 *
 * Bins of width 0.25 R from -3 to +3; anything outside lands in the
 * outermost bin.
 */
export function rDistribution(rows: ScalpPosition[]): RBin[] {
  const bins = new Map<number, RBin>();
  // Pre-create bins so empty buckets render at zero.
  const edges: number[] = [];
  for (let v = -3; v < 3 + 1e-9; v += 0.25) {
    edges.push(round2(v));
  }
  for (const v of edges) {
    bins.set(v, {
      bin: `[${v.toFixed(2)}, ${(v + 0.25).toFixed(2)})`,
      midpoint: round2(v + 0.125),
      count: 0,
      positive: v + 0.125 > 0,
    });
  }

  for (const r of rows) {
    if (r.net_pnl_usd == null || !r.qty || !r.entry_price || !r.stop_price) continue;
    const risk = (r.entry_price - r.stop_price) * r.qty;
    if (risk <= 0) continue;
    const rMul = Number(r.net_pnl_usd) / risk;
    const clamped = Math.max(-3, Math.min(rMul, 2.999));
    const bucket = Math.floor(clamped * 4) / 4; // round down to 0.25
    const key = round2(Math.max(-3, bucket));
    const b = bins.get(key);
    if (b) b.count += 1;
  }
  return [...bins.values()];
}

/** Per-symbol P&L for the trades-by-symbol summary row. */
export interface SymbolStat {
  symbol: string;
  trades: number;
  buyUsd: number;
  sellUsd: number;
  fees: number;
  netFlow: number; // sellUsd - buyUsd - fees (rough; doesn't track inventory)
}

export function tradesBySymbol(rows: BackendTrade[]): SymbolStat[] {
  const map = new Map<string, SymbolStat>();
  for (const t of rows) {
    const sym = t.symbol;
    const cur = map.get(sym) ?? {
      symbol: sym,
      trades: 0,
      buyUsd: 0,
      sellUsd: 0,
      fees: 0,
      netFlow: 0,
    };
    cur.trades += 1;
    cur.fees += Number(t.fee ?? 0);
    if (t.action === "BUY") cur.buyUsd += Number(t.size_usd ?? 0);
    else if (t.action === "SELL") cur.sellUsd += Number(t.size_usd ?? 0);
    map.set(sym, cur);
  }
  for (const v of map.values()) v.netFlow = round2(v.sellUsd - v.buyUsd - v.fees);
  return [...map.values()].sort((a, b) => b.trades - a.trades);
}

/** 7×24 grid: number of cycles per (day-of-week, hour-of-day) UTC. */
export function cycleHeatmap(rows: BackendCycle[]): number[][] {
  const grid: number[][] = Array.from({ length: 7 }, () => Array(24).fill(0));
  for (const c of rows) {
    if (!c.started_at) continue;
    const d = new Date(c.started_at);
    if (Number.isNaN(d.getTime())) continue;
    const dow = d.getUTCDay();
    const hour = d.getUTCHours();
    grid[dow][hour] += 1;
  }
  return grid;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
